import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";

import { useMemo } from "react";
import { cx } from "shared/utils";
import {
  CodeAndDescription,
  CreditReportAccountOverview
} from "types/landlord/reports";
import { Check, DotsHorizontal } from "untitledui-js-base";

const PaymentHistoryStatus = {
  "*": " ",
  0: <DotsHorizontal size="20px" className="stroke-warning-600" />,
  1: <Check size="20px" className="stroke-success-700" />,
  E: <Check size="20px" className="stroke-success-700" />,
  no_data: ""
};

interface PaymentHistoryProps {
  payment_history: CreditReportAccountOverview["payment_history"];
  date_reported: string;
}

export const PaymentHistory = ({
  payment_history,
  date_reported
}: PaymentHistoryProps) => {
  const monthReported = new Date(date_reported).getMonth();

  const data = useMemo(() => {
    const filtered_payment_history =
      payment_history?.filter((el) => el.code !== "/") || [];

    if (!filtered_payment_history.length) return [];

    let ret = filtered_payment_history;

    if (monthReported !== 11) {
      const prevMonthLength = 11 - monthReported >= 0 ? 11 - monthReported : 0;
      const prevMonth = Array(prevMonthLength).fill({
        code: "no_data",
        description: "No payment history"
      });
      const nextMonthLength = monthReported + 1 >= 0 ? monthReported + 1 : 0;
      const nextMonth = Array(nextMonthLength).fill({
        code: "no_data",
        description: "No payment history"
      });

      ret = [...prevMonth, ...filtered_payment_history, ...nextMonth];
    }

    const inversed: CodeAndDescription[] = [];

    for (let i = 0; i < ret.length; i = i + 12)
      inversed.push(...ret.slice(i, i + 12).reverse());

    return inversed;
  }, [payment_history]);

  return data.map((el: CodeAndDescription, idx) => {
    // * and "" are the same
    const code = el.code === "" ? "*" : String(el.code);

    const cellClx = () => {
      if (code === "no_data") return "bg-alpha-80";
      if (["1", "E"].includes(code)) return "bg-success-50 text-success-600";
      if (code === "0") return "bg-warning-50 text-warning-600";
      if (["2", "3", "4", "5", "6", "7", "8", "9"].includes(code))
        return "bg-danger-50 text-danger-500";

      return "bg-alpha-40 text-content-1000";
    };

    return (
      <Tooltip key={code + idx}>
        <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
          {el.description}
        </TooltipContent>
        <TooltipTrigger>
          <div
            className={cx(
              "flex h-10 w-10 items-center justify-center rounded-lg text-sm font-bold",
              cellClx()
            )}
          >
            {PaymentHistoryStatus[el.code || ""] ?? el.code}
          </div>
        </TooltipTrigger>
      </Tooltip>
    );
  });
};
